import React, { useState, useEffect, useCallback } from "react";
import "../../Complaint.css";
import "bootstrap/dist/css/bootstrap.min.css";
import CommonContainer from "../Container/CommonContainer";
import toast from "react-hot-toast";
import * as PartnerService from '../../Services/Partner/PartnerService';
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AA46E5"];
const chartTypes = ["Bar", "Line", "Area", "Pie"];
const filters = ["Current Week", "Last 15 Days", "MTD", "Last 30 Days"];

const Graph = () => {
    const [startDate, setStartDate] = useState(() => dayjs().startOf("month").toDate());
    const [endDate, setEndDate] = useState(() => dayjs().endOf("day").toDate());
    const [patientReportList, setPatientReportList] = useState([]);
    const [activeFilter, setActiveFilter] = useState("MTD");
    const [chartType, setChartType] = useState("Bar");
    const [filteredData, setFilteredData] = useState([]);

    useEffect(() => {
        //debugger;
        fetchPatientReportList();
    }, []);

    useEffect(() => {
        applyFilter();
    }, [activeFilter, patientReportList]);
    const fetchPatientReportList = useCallback(() => {
        const requestData = {
            StartDate: new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
                0, 0, 0, 0
            ).toISOString(),
            EndDate: new Date(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate(),
                23, 59, 59, 999
            ).toISOString(),
            Org_Id: "All",
        };

        PartnerService.GetPatientVisitDataList(requestData)
            .then(({ data }) => {
                if (data.IsSuccess) {
                    setPatientReportList(data.Data);
                } else {
                    setPatientReportList([]);
                }
            })
            .catch(() => toast.error("Something went wrong."));
    }, [startDate, endDate]);

    const applyFilter = () => {
        const now = dayjs();
        let filtered = patientReportList;

        filtered = filtered.filter(item => {
            const billDate = dayjs(item.MHL_Visit_Date__c);
            switch (activeFilter) {
                case "Current Week": return billDate.isSame(now, 'week');
                case "Last 15 Days": return billDate.isAfter(now.subtract(15, 'day'));
                case "Last 30 Days": return billDate.isAfter(now.subtract(30, 'day'));
                case "MTD": return billDate.isSame(now, 'month');
                default: return false;
            }
        });

        const aggregated = {};
        filtered.forEach(item => {
            const date = dayjs(item.MHL_Visit_Date__c).format("YYYY-MM-DD");
            const amount = parseFloat(item.MHL_Net_Amount__c || 0);
            aggregated[date] = (aggregated[date] || 0) + amount;
        });

        const chartData = Object.entries(aggregated).map(([date, amount]) => ({
            date,
            netAmount: amount,
        }));

        setFilteredData(chartData);
    };

    const renderChart = () => {
        if (chartType === "Bar") {
            return (
                <BarChart data={filteredData} width={800} height={300}>
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="netAmount" fill="#8884d8" />
                </BarChart>
            );
        } else if (chartType === "Line") {
            return (
                <LineChart data={filteredData} width={800} height={300}>
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="netAmount" stroke="#8884d8" />
                </LineChart>
            );
        } else if (chartType === "Area") {
            return (
                <AreaChart data={filteredData} width={800} height={300}>
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="netAmount" stroke="#82ca9d" fill="#82ca9d" />
                </AreaChart>
            );
        } else if (chartType === "Pie") {
            return (
                <PieChart width={400} height={300}>
                    <Pie
                        data={filteredData}
                        dataKey="netAmount"
                        nameKey="date"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        label
                    >
                        {filteredData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip />
                </PieChart>
            );
        }
    };

    return (
        <CommonContainer heading="Graph">
            <div className="mb-3">
                <div className="btn-group mb-3">
                    {filters.map(f => (
                        <button
                            key={f}
                            onClick={() => setActiveFilter(f)}
                            className={`btn btn-${activeFilter === f ? "primary" : "outline-primary"}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
                <div className="btn-group">
                    {chartTypes.map(type => (
                        <button
                            key={type}
                            onClick={() => setChartType(type)}
                            className={`btn btn-${chartType === type ? "success" : "outline-success"}`}
                        >
                            {type} Chart
                        </button>
                    ))}
                </div>
            </div>
            <ResponsiveContainer width="100%" height={400}>
                {renderChart()}
            </ResponsiveContainer>
        </CommonContainer>
    );
};

export default Graph;